import { useMemo, useRef, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import type { PMPartsAndProcedure, PMServiceLevel } from '../../types/pmTypes';
import { useLockScrollAxis } from '../../hooks/useLockScrollAxis';
import { isProcedureType } from '../../utils/companyResourceUtils';
import { getInheritedMatrixAssignment, parseMatrixQuantityDraft } from '../../utils/pmFormUtils';
import {
    BtnIconOnly,
    DataTable,
    DataTableNoRowDrag,
    DataTableSortableHandle,
    IconClose,
    IconDragHandle,
    IconPlus,
    type DataTableColumn,
} from '../ui';
import { compactPmUnitLabel } from './pmConstants';

const MATRIX_SORT_INSTRUCTIONS_ID = 'pm-matrix-sortable-instructions';

type MatrixPartRow = { rowType: 'part'; part: PMPartsAndProcedure };
type MatrixHintRow = { rowType: 'hint'; id: string; hintKind: 'consumable' | 'procedure' };
export type ServiceMatrixRow = MatrixPartRow | MatrixHintRow;

const HINT = {
    consumable: {
        badge: '$ Con',
        name: 'Consumable name',
        meta: 'Qty per level',
        isProcedure: false,
    },
    procedure: {
        badge: 'Proc',
        name: 'Procedure name',
        meta: 'Check to perform',
        isProcedure: true,
    },
} as const;

function MatrixCheckIcon() {
    return (
        <svg className="pm-matrix-check-icon" viewBox="0 0 16 16" aria-hidden="true">
            <path
                d="M3.2 8.4 6.4 11.6 12.8 4.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function scrollMatrixQuantityIntoView(input: HTMLInputElement) {
    const container = input.closest('.data-table-wrapper');
    const row = input.closest('tr');
    if (!(container instanceof HTMLElement) || !(row instanceof HTMLElement)) return;

    const rowRect = row.getBoundingClientRect();
    const box = container.getBoundingClientRect();
    const headerAllowance = 48;
    if (rowRect.bottom > box.bottom - 8) {
        container.scrollTop += rowRect.bottom - box.bottom + 16;
    } else if (rowRect.top < box.top + headerAllowance) {
        container.scrollTop -= box.top + headerAllowance - rowRect.top;
    }

    const inputRect = input.getBoundingClientRect();
    if (inputRect.right > box.right - 8) {
        container.scrollLeft += inputRect.right - box.right + 16;
    } else if (inputRect.left < box.left + 160) {
        container.scrollLeft -= box.left + 160 - inputRect.left;
    }
}

export interface ServiceMatrixTableProps {
    parts: PMPartsAndProcedure[];
    serviceLevels: PMServiceLevel[];
    localAssignments: Record<string, Record<string, boolean | number>>;
    qtyDrafts: Record<string, string>;
    setQtyDrafts: Dispatch<SetStateAction<Record<string, string>>>;
    unitOfMeasure: string;
    unitOfMeasureLabel: string;
    canManage: boolean;
    onToggleProcedure: (levelId: string, partId: string) => void;
    onQtyChange: (levelId: string, partId: string, qty: number) => void;
    onAddLevel: () => void;
    onRemovePart: (part: { id: string; name: string }) => void;
    onReorderParts: (activeId: string, overId: string) => void;
    scrollWrapperRef?: (node: HTMLDivElement | null) => void;
}

export function ServiceMatrixTable({
    parts,
    serviceLevels,
    localAssignments,
    qtyDrafts,
    setQtyDrafts,
    unitOfMeasure,
    unitOfMeasureLabel,
    canManage,
    onToggleProcedure,
    onQtyChange,
    onAddLevel,
    onRemovePart,
    onReorderParts,
    scrollWrapperRef,
}: ServiceMatrixTableProps) {
    const umSuffix = compactPmUnitLabel(unitOfMeasure) || unitOfMeasureLabel;

    const rows: ServiceMatrixRow[] = useMemo(() => {
        if (parts.length === 0) {
            return [
                { rowType: 'hint', id: '__hint_consumable', hintKind: 'consumable' },
                { rowType: 'hint', id: '__hint_procedure', hintKind: 'procedure' },
            ];
        }
        return parts.map((part) => ({ rowType: 'part' as const, part }));
    }, [parts]);

    const renderLevelCell = (part: PMPartsAndProcedure, sl: PMServiceLevel): ReactNode => {
        const isProcedure = isProcedureType(part.itemType);
        const val = localAssignments[sl.id]?.[part.id];
        const hasOwn = isProcedure ? !!val : typeof val === 'number' && val > 0;
        const inherited = getInheritedMatrixAssignment(sl, part.id, serviceLevels, localAssignments);
        const showInherited = Boolean(inherited) && !hasOwn;
        const isActive = hasOwn || showInherited;
        const qtyUnit = compactPmUnitLabel(part.unitOfMeasure);
        const qtyDraftKey = `${sl.id}:${part.id}`;
        const qtyDraft = qtyDrafts[qtyDraftKey];
        const inheritedFrom = inherited ? inherited.sourceLevelIdentifiers.join(', ') : '';
        const inheritedTitle = showInherited
            ? `Included from nested level${inherited!.sourceLevelIdentifiers.length === 1 ? '' : 's'} ${inheritedFrom}`
            : undefined;

        return (
            <div
                className={`pm-matrix-cell-inner${showInherited ? ' pm-matrix-cell-inner--inherited' : ''}`}
                title={inheritedTitle}
            >
                {isProcedure ? (
                    showInherited || !canManage ? (
                        <span
                            className={`pm-matrix-check-box ${isActive ? 'checked' : 'empty'}${showInherited ? ' pm-matrix-check-box--inherited' : ''}`}
                            aria-label={
                                showInherited
                                    ? `${part.itemName} at ${sl.levelIdentifier}: included from ${inheritedFrom}`
                                    : `${part.itemName} at ${sl.levelIdentifier}: ${isActive ? 'assigned' : 'not assigned'}`
                            }
                        >
                            {isActive ? <MatrixCheckIcon /> : null}
                        </span>
                    ) : (
                        <button
                            type="button"
                            className={`pm-matrix-check-box clickable ${isActive ? 'checked' : 'empty'}`}
                            onClick={() => onToggleProcedure(sl.id, part.id)}
                            aria-label={`${isActive ? 'Remove' : 'Assign'} ${part.itemName} to ${sl.levelIdentifier}`}
                        >
                            {isActive ? <MatrixCheckIcon /> : null}
                        </button>
                    )
                ) : showInherited ? (
                    <span
                        className="pm-matrix-qty-well pm-matrix-qty-well--inherited"
                        aria-label={`${part.itemName} quantity at ${sl.levelIdentifier}: ${inherited!.quantity}${qtyUnit ? ` ${qtyUnit}` : ''} (included from ${inheritedFrom})`}
                    >
                        <span className="pm-matrix-qty-input">{String(inherited!.quantity)}</span>
                        {qtyUnit ? <span className="pm-matrix-qty-unit">{qtyUnit}</span> : null}
                    </span>
                ) : canManage ? (
                    <label className="pm-matrix-qty-well">
                        <input
                            type="text"
                            inputMode="decimal"
                            className="pm-matrix-qty-input"
                            value={
                                qtyDraft !== undefined
                                    ? qtyDraft
                                    : typeof val === 'number' && val > 0
                                      ? String(val)
                                      : ''
                            }
                            onChange={(e) => {
                                const raw = e.target.value;
                                const parsed = parseMatrixQuantityDraft(raw);
                                if (!parsed.accepted) return;
                                setQtyDrafts((prev) => ({ ...prev, [qtyDraftKey]: raw }));
                                onQtyChange(sl.id, part.id, parsed.quantity);
                            }}
                            onBlur={() => {
                                setQtyDrafts((prev) => {
                                    if (!(qtyDraftKey in prev)) return prev;
                                    const next = { ...prev };
                                    delete next[qtyDraftKey];
                                    return next;
                                });
                            }}
                            aria-label={`${part.itemName} quantity at ${sl.levelIdentifier}${qtyUnit ? ` (${qtyUnit})` : ''}`}
                            onFocus={(event) => {
                                scrollMatrixQuantityIntoView(event.currentTarget);
                            }}
                        />
                        {qtyUnit ? <span className="pm-matrix-qty-unit">{qtyUnit}</span> : null}
                    </label>
                ) : (
                    <span className="pm-matrix-qty-well">
                        <span className="pm-matrix-qty-input">
                            {typeof val === 'number' && val > 0 ? String(val) : ''}
                        </span>
                        {qtyUnit ? <span className="pm-matrix-qty-unit">{qtyUnit}</span> : null}
                    </span>
                )}
            </div>
        );
    };

    const columns = useMemo((): DataTableColumn<ServiceMatrixRow>[] => {
        const itemCol: DataTableColumn<ServiceMatrixRow> = {
            id: 'item',
            header: 'Item / Procedure',
            sticky: 'start',
            thClassName: 'pm-matrix-item-col',
            tdClassName: 'pm-matrix-item-cell',
            renderCell: (row) => {
                if (row.rowType === 'hint') {
                    const hint = HINT[row.hintKind];
                    return (
                        <div className="pm-matrix-item-row" aria-hidden="true">
                            <span className="pm-matrix-drag-handle">
                                <IconDragHandle />
                            </span>
                            <span
                                className={`pm-matrix-item-type ${hint.isProcedure ? 'pm-matrix-item-type--proc' : 'pm-matrix-item-type--con'}`}
                            >
                                {hint.badge}
                            </span>
                            <div className="pm-matrix-item-info">
                                <span className="pm-matrix-item-name">{hint.name}</span>
                                <span className="pm-matrix-item-meta">{hint.meta}</span>
                            </div>
                        </div>
                    );
                }

                const { part } = row;
                const isProcedure = isProcedureType(part.itemType);
                return (
                    <div className="pm-matrix-item-row" id={`pm-matrix-row-${part.id}`}>
                        {canManage ? (
                            <DataTableSortableHandle
                                className="pm-matrix-drag-handle"
                                ariaLabel={`Reorder ${part.itemName}`}
                            >
                                <IconDragHandle />
                            </DataTableSortableHandle>
                        ) : null}
                        <span
                            className={`pm-matrix-item-type ${isProcedure ? 'pm-matrix-item-type--proc' : 'pm-matrix-item-type--con'}`}
                        >
                            {isProcedure ? 'Proc' : '$ Con'}
                        </span>
                        <div className="pm-matrix-item-info">
                            <span className="pm-matrix-item-name">{part.itemName}</span>
                            {!isProcedure && (part.partNumber || part.manufacturer) ? (
                                <span className="pm-matrix-item-meta">
                                    {[part.partNumber, part.manufacturer].filter(Boolean).join(' · ')}
                                </span>
                            ) : null}
                            {isProcedure && part.category ? (
                                <span className="pm-matrix-item-meta">{part.category}</span>
                            ) : null}
                        </div>
                    </div>
                );
            },
        };

        const levelCols: DataTableColumn<ServiceMatrixRow>[] = serviceLevels.map((sl) => ({
            id: `level-${sl.id}`,
            header: (
                <div className="pm-matrix-level-head">
                    <span className="pm-matrix-level-name">{sl.levelIdentifier}</span>
                    <span className="pm-matrix-interval">
                        {sl.intervalValue.toLocaleString()} {umSuffix}
                    </span>
                </div>
            ),
            headerLabel: `${sl.levelIdentifier} ${sl.intervalValue.toLocaleString()} ${umSuffix}`,
            align: 'center',
            thClassName: 'pm-matrix-level-col',
            tdClassName: 'pm-matrix-cell',
            renderCell: (row) => {
                if (row.rowType === 'hint') {
                    const hint = HINT[row.hintKind];
                    return hint.isProcedure ? (
                        <span className="pm-matrix-check-box empty" aria-hidden="true" />
                    ) : (
                        <span className="pm-matrix-qty-well" aria-hidden="true" />
                    );
                }
                return renderLevelCell(row.part, sl);
            },
        }));

        const cols = [itemCol, ...levelCols];

        if (canManage) {
            cols.push({
                id: 'add-level',
                header: (
                    <button type="button" className="pm-matrix-add-level-cell" onClick={onAddLevel}>
                        <IconPlus />
                        <span>Add Level</span>
                    </button>
                ),
                headerLabel: 'Add Level',
                thClassName: 'pm-matrix-add-level-col',
                tdClassName: 'pm-matrix-add-level-col',
                align: 'center',
                renderCell: () => null,
            });
            cols.push({
                id: 'actions',
                header: <span className="data-table-visually-hidden">Remove</span>,
                headerLabel: 'Remove',
                thClassName: 'pm-matrix-remove-col',
                tdClassName: 'pm-matrix-remove-cell',
                align: 'center',
                renderCell: (row) => {
                    if (row.rowType === 'hint') return null;
                    return (
                        <DataTableNoRowDrag>
                            <BtnIconOnly
                                size="sm"
                                variant="danger"
                                icon={<IconClose />}
                                ariaLabel={`Remove ${row.part.itemName} from matrix`}
                                onClick={() =>
                                    onRemovePart({ id: row.part.id, name: row.part.itemName })
                                }
                            />
                        </DataTableNoRowDrag>
                    );
                },
            });
        }

        return cols;
        // renderLevelCell closes over latest drafts/assignments; rebuild when those change.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional full rebuild on matrix inputs
    }, [
        canManage,
        localAssignments,
        onAddLevel,
        onQtyChange,
        onRemovePart,
        onToggleProcedure,
        qtyDrafts,
        serviceLevels,
        setQtyDrafts,
        umSuffix,
    ]);

    const sortableRowIds = canManage && parts.length > 0 ? parts.map((part) => part.id) : undefined;
    const containerRef = useRef<HTMLDivElement | null>(null);
    useLockScrollAxis(
        containerRef,
        '.pm-matrix-data-table-scroll',
        `${parts.length}-${serviceLevels.length}-${canManage ? 1 : 0}`,
    );

    const setContainerRef = (node: HTMLDivElement | null) => {
        containerRef.current = node;
        scrollWrapperRef?.(node);
    };

    return (
        <div className="pm-matrix-container" ref={setContainerRef}>
            <p id={MATRIX_SORT_INSTRUCTIONS_ID} className="data-table-visually-hidden">
                Reorder matrix rows with the drag handle. Press Space or Enter to pick up a row, Arrow keys to move
                it, Space or Enter to drop, Escape to cancel.
            </p>
            <DataTable
                className="pm-matrix-data-table-scroll"
                tableClassName="pm-matrix-data-table"
                theadClassName="pm-matrix-header-dark"
                ariaLabel="Service Matrix"
                scrollRegionLabel="Service Matrix"
                scrollable
                maxBodyHeight="min(70vh, 720px)"
                mobileCardLayout={false}
                columns={columns}
                rows={rows}
                getRowKey={(row) => (row.rowType === 'hint' ? row.id : row.part.id)}
                sortableRowIds={sortableRowIds}
                onSortableRowDragEnd={onReorderParts}
                sortableInstructionsId={MATRIX_SORT_INSTRUCTIONS_ID}
                getSortableRowAriaLabel={({ row }) =>
                    row.rowType === 'part' ? `Reorder ${row.part.itemName}` : 'Matrix hint row'
                }
                rowClassName={(row) => (row.rowType === 'hint' ? 'pm-matrix-hint-row' : undefined)}
            />
        </div>
    );
}
